import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, IconButton, TextField } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import FlightIcon from '@mui/icons-material/Flight';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { COLORS } from '../styles';

const KIND_META = {
  dispatcher: { icon: SupportAgentIcon, color: COLORS.warn },
  flight:     { icon: FlightIcon,        color: COLORS.accent },
  system:     { icon: NotificationsIcon, color: COLORS.textSecondary },
};

function MessagesScreen({ threads, messagesByThread, onSendReply, onMarkRead }) {
  const [openThreadId, setOpenThreadId] = useState(null);

  const openThread = openThreadId ? threads.find(t => t.id === openThreadId) : null;

  useEffect(() => {
    if (openThreadId) onMarkRead(openThreadId);
  }, [openThreadId, onMarkRead]);

  if (openThread) {
    return (
      <ThreadView
        thread={openThread}
        messages={messagesByThread[openThread.id] || []}
        onBack={() => setOpenThreadId(null)}
        onSendReply={(body) => onSendReply(openThread.id, body)}
      />
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, pb: 1.5, borderBottom: `1px solid ${COLORS.divider}` }}>
        <Typography sx={{ fontSize: '0.6rem', color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.6 }}>
          Inbox
        </Typography>
        <Typography sx={{ fontSize: '1.2rem', color: COLORS.textPrimary, fontWeight: 700, lineHeight: 1.1 }}>
          Messages
        </Typography>
      </Box>

      {/* Thread list */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {threads.map((t) => {
          const msgs = messagesByThread[t.id] || [];
          const latest = msgs[msgs.length - 1];
          const unread = msgs.filter(m => !m.read && m.sender !== 'driver').length;
          const meta = KIND_META[t.kind] || KIND_META.system;
          const Icon = meta.icon;

          return (
            <Box
              key={t.id}
              onClick={() => setOpenThreadId(t.id)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                p: 1.5,
                borderBottom: `1px solid ${COLORS.divider}`,
                cursor: 'pointer',
                '&:active': { backgroundColor: COLORS.surfaceAlt },
              }}
            >
              <Box sx={{
                width: 36, height: 36,
                borderRadius: '50%',
                backgroundColor: `${meta.color}22`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Icon sx={{ color: meta.color, fontSize: 20 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
                  <Typography
                    noWrap
                    sx={{
                      fontSize: '0.82rem', color: COLORS.textPrimary,
                      fontWeight: unread > 0 ? 700 : 600,
                    }}
                  >
                    {t.label}
                  </Typography>
                  {latest && (
                    <Typography sx={{ fontSize: '0.6rem', color: COLORS.textMuted, flexShrink: 0 }}>
                      {latest.time}
                    </Typography>
                  )}
                </Box>
                {t.sublabel && (
                  <Typography noWrap sx={{ fontSize: '0.6rem', color: COLORS.textMuted }}>
                    {t.sublabel}
                  </Typography>
                )}
                <Typography
                  noWrap
                  sx={{
                    fontSize: '0.7rem',
                    color: unread > 0 ? COLORS.textPrimary : COLORS.textMuted,
                    fontWeight: unread > 0 ? 600 : 400,
                  }}
                >
                  {latest ? `${latest.sender === 'driver' ? 'You: ' : ''}${latest.body}` : 'No messages'}
                </Typography>
              </Box>
              {unread > 0 && (
                <Box sx={{
                  minWidth: 18, height: 18,
                  px: 0.5,
                  borderRadius: '50%',
                  backgroundColor: COLORS.danger,
                  color: '#fff',
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {unread}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function ThreadView({ thread, messages, onBack, onSendReply }) {
  const meta = KIND_META[thread.kind] || KIND_META.system;
  const Icon = meta.icon;
  const [draft, setDraft] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  const send = () => {
    const body = draft.trim();
    if (!body) return;
    onSendReply(body);
    setDraft('');
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 1.5, borderBottom: `1px solid ${COLORS.divider}`, display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton size="small" onClick={onBack} sx={{ color: COLORS.textSecondary, p: 0.5 }}>
          <ArrowBackIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <Box sx={{
          width: 28, height: 28,
          borderRadius: '50%',
          backgroundColor: `${meta.color}22`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Icon sx={{ color: meta.color, fontSize: 16 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: '0.85rem', color: COLORS.textPrimary, fontWeight: 700, lineHeight: 1.1 }}>
            {thread.label}
          </Typography>
          {thread.sublabel && (
            <Typography noWrap sx={{ fontSize: '0.6rem', color: COLORS.textMuted }}>
              {thread.sublabel}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Messages */}
      <Box ref={scrollRef} sx={{ flex: 1, overflow: 'auto', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {messages.length === 0 && (
          <Typography sx={{ fontSize: '0.75rem', color: COLORS.textMuted, fontStyle: 'italic', textAlign: 'center', mt: 4 }}>
            No messages yet.
          </Typography>
        )}
        {messages.map((m, i) => {
          const isDriver = m.sender === 'driver';
          const isSystem = m.sender === 'system';
          if (isSystem) {
            return (
              <Box key={i} sx={{ alignSelf: 'center', textAlign: 'center', maxWidth: '90%' }}>
                <Typography sx={{
                  fontSize: '0.65rem',
                  color: COLORS.textMuted,
                  fontStyle: 'italic',
                  py: 0.25,
                }}>
                  {m.body} · {m.time}
                </Typography>
              </Box>
            );
          }
          return (
            <Box
              key={i}
              sx={{
                alignSelf: isDriver ? 'flex-end' : 'flex-start',
                maxWidth: '78%',
                backgroundColor: isDriver ? COLORS.accent : COLORS.surface,
                color: isDriver ? '#0a1020' : COLORS.textPrimary,
                px: 1.25,
                py: 0.75,
                borderRadius: 2,
                borderTopRightRadius: isDriver ? 4 : 16,
                borderTopLeftRadius: isDriver ? 16 : 4,
              }}
            >
              {!isDriver && m.senderName && (
                <Typography sx={{ fontSize: '0.55rem', color: meta.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {m.senderName}
                </Typography>
              )}
              <Typography sx={{ fontSize: '0.78rem', lineHeight: 1.35 }}>
                {m.body}
              </Typography>
              <Typography sx={{
                fontSize: '0.55rem',
                color: isDriver ? 'rgba(15,23,42,0.5)' : COLORS.textMuted,
                textAlign: 'right',
                mt: 0.25,
              }}>
                {m.time}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Composer (no compose for system thread) */}
      {thread.kind !== 'system' && (
        <Box sx={{ p: 1, borderTop: `1px solid ${COLORS.divider}`, display: 'flex', gap: 0.5, alignItems: 'flex-end' }}>
          <TextField
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Type a message..."
            size="small"
            fullWidth
            multiline
            maxRows={3}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: COLORS.surface,
                color: COLORS.textPrimary,
                fontSize: '0.78rem',
                '& fieldset': { borderColor: COLORS.divider },
                '&:hover fieldset': { borderColor: COLORS.accentDim },
                '&.Mui-focused fieldset': { borderColor: COLORS.accent },
              },
            }}
          />
          <IconButton
            onClick={send}
            disabled={!draft.trim()}
            sx={{
              backgroundColor: COLORS.accent,
              color: '#0a1020',
              '&:hover': { backgroundColor: COLORS.accentDim },
              '&.Mui-disabled': { backgroundColor: COLORS.surfaceAlt, color: COLORS.textMuted },
            }}
          >
            <SendIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
}

export default MessagesScreen;
